import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Calendar, Car, Info, User, ShoppingBag, Ticket, MapPin, UtensilsCrossed, ShoppingCart } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useAuthStore } from '../store/auth';
import { useCartStore } from '../store/cart';
import { useSettingsStore } from '../store/settings';
import { cn } from '../lib/utils';

const allNavLinks = [
  { href: '/', label: 'Home', icon: Home, feature: null },
  { href: '/track-rentals', label: 'Tracks', icon: Car, feature: null },
  { href: '/calendar', label: 'Calendar', icon: Calendar, feature: null },
  { href: '/tickets', label: 'Tickets', icon: Ticket, feature: 'tickets' },
  { href: '/shop', label: 'Shop', icon: ShoppingBag, feature: 'shop' },
  { href: '/food', label: 'Food', icon: UtensilsCrossed, feature: 'food' },
  { href: '/map', label: 'Map', icon: MapPin, feature: 'map' },
];

export function PublicLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user } = useAuthStore();
  const { totalItems } = useCartStore();
  const { foodOrderingEnabled, shopEnabled, ticketsEnabled, mapEnabled } = useSettingsStore();
  const location = useLocation();
  const cartCount = totalItems();

  // Filter nav links based on enabled features
  const navLinks = useMemo(() => {
    return allNavLinks.filter((link) => {
      if (link.feature === null) return true;
      if (link.feature === 'food') return foodOrderingEnabled;
      if (link.feature === 'shop') return shopEnabled;
      if (link.feature === 'tickets') return ticketsEnabled;
      if (link.feature === 'map') return mapEnabled;
      return true;
    });
  }, [foodOrderingEnabled, shopEnabled, ticketsEnabled, mapEnabled]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">D</span>
              </div>
              <span className="font-display text-xl text-gray-900">DriveIT NQ</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'text-sm font-medium transition-colors',
                    location.pathname === link.href
                      ? 'text-red-600'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Cart Button */}
              <Link
                to="/cart"
                className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ShoppingCart size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-medium">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>

              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="btn btn-primary btn-md"
                >
                  {user?.firstName ? `Hi, ${user.firstName}` : 'Dashboard'}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-primary btn-md"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white animate-slide-down">
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    'flex items-center space-x-3 px-4 py-3 rounded-lg',
                    location.pathname === link.href
                      ? 'bg-red-50 text-red-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <link.icon size={20} />
                  <span className="font-medium">{link.label}</span>
                </Link>
              ))}

              <div className="pt-4 border-t border-gray-200 mt-4">
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg bg-red-600 text-white"
                  >
                    <User size={20} />
                    <span className="font-medium">Dashboard</span>
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-4 py-3 text-center text-gray-600 font-medium"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMenuOpen(false)}
                      className="block px-4 py-3 text-center bg-red-600 text-white rounded-lg font-medium"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">D</span>
                </div>
                <span className="font-display text-xl text-white">DriveIT NQ</span>
              </div>
              <p className="text-sm">
                North Queensland's Premier Motorsport Facility
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/track-rentals" className="hover:text-white">Track Rentals</Link></li>
                <li><Link to="/calendar" className="hover:text-white">Events Calendar</Link></li>
                <li><Link to="/book" className="hover:text-white">Book Now</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">About</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">Our Story</Link></li>
                <li><Link to="/about" className="hover:text-white">The Team</Link></li>
                <li><Link to="/about" className="hover:text-white">STEM Programs</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>info@driveitnq.com.au</li>
                <li>07 4000 0000</li>
                <li>123 Raceway Drive<br />Townsville QLD 4810</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} DriveIT NQ. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
